import { useState } from 'react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import {
  Box,
  Icon,
  Link,
  Panel,
  Strong,
  PanelHeader,
  PanelSection,
  PanelTitle,
  Paragraph,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
} from '@customerio/pluma-components/react';
import { CredentialKeyPair, CredentialsSelect } from '@/features/integrations';
import { useListWorkspaces } from '@/features/workspaces';
import { useAnalytics } from '@/services/analytics';

import type { ApiCredential } from '@/services/db/api-credential/entity';
import type { SegmentInConnection } from '@/services/db/segment-in-integration/queries';
import {
  ConnectionState,
  connectionButtonLabel,
  connectionDescriptionPrefix,
  parseSegmentSourceName,
  resolveSelectedCredentialId,
} from '../connection-status';
import { clearStartedProperties, disableStartedProperties } from '../analytics';
import { useSegmentConnection } from '../use-segment-connection';
import { SegmentActionsRow } from './segment-actions-row';
import { SegmentInBanners } from './segment-in-banners';
import { SegmentConnectionModals, type SegmentModal } from './segment-connection-modals';
import { SegmentConnectionSummary } from './segment-connection-summary';

const API_CREDENTIALS_HREF = '/settings/api_credentials';
const CREDENTIAL_KEY_PAIR_SOURCE = 'Integration Page (Segment Destination)';

type SegmentInConnectorProps = {
  workspaceId: string;
  workspaceName: string;
  connection: SegmentInConnection | null;
  credentials: readonly ApiCredential[];
};

/**
 * The connector panel: which credentials the destination uses, and the action the
 * connection state implies.
 *
 * The credential picker is disabled when the workspace has exactly one, because
 * there is nothing to choose between — that credential is used regardless.
 */
export function SegmentInConnector({ workspaceId, workspaceName, connection, credentials }: SegmentInConnectorProps) {
  const { hasPermission } = usePermissions();
  const { track } = useAnalytics();
  // Only to name the workspace that already holds a clashing Segment source.
  const { data: workspaces = [] } = useListWorkspaces();
  const canIntegrate = hasPermission('journeys/segment_integration/create');
  const canEdit = hasPermission('journeys/segment_integration/edit');
  const canDelete = hasPermission('journeys/segment_integration/delete');
  const [chosenCredentialId, setChosenCredentialId] = useState<string | null>(null);
  const [openModal, setOpenModal] = useState<SegmentModal>(null);

  const selectedCredentialId = resolveSelectedCredentialId(credentials, chosenCredentialId);
  const selectedCredential = credentials.find((credential) => credential.id === selectedCredentialId) ?? null;

  const hasSingleCredential = credentials.length === 1;
  const { state, alert, clearError, dismissClearError, dismissAlert, isRunning, connect, disable, remove, clear } =
    useSegmentConnection(workspaceId, connection != null);

  const connectedSource = parseSegmentSourceName(connection?.source);
  // Ember hid this while the state was unknown or deleted: a stale timestamp for a
  // connection that is gone reads as though it were still live.
  const isVerificationMeaningful = state !== ConnectionState.Unknown && state !== ConnectionState.Deleted;
  const lastVerifiedAt = isVerificationMeaningful ? (connection?.updated_at ?? null) : null;

  return (
    <>
      <SegmentInBanners
        alert={alert}
        workspaces={workspaces}
        credentials={credentials}
        clearError={clearError}
        retryLabel={connectionButtonLabel(state)}
        onDismissAlert={dismissAlert}
        onDismissClearError={dismissClearError}
      />

      <Panel>
        <PanelHeader aria-labelledby="data-in-title">
          <PanelTitle level="2" id="data-in-title">
            Get data into Customer.io
          </PanelTitle>
        </PanelHeader>
        <PanelSection>
          <Paragraph m="0" data-test-segment-in-description>
            {connectionDescriptionPrefix(state)} <Strong>{workspaceName}</Strong> workspace using the following API
            credentials{' '}
            <Tooltip>
              <Icon name="help" />
              <TooltipContent>
                <Paragraph color="inherit" m="0">
                  API credentials provide access to send information about your customers to Customer.io. By default,
                  each workspace has an API credential created. You can create more API credentials within your{' '}
                  <Link href={API_CREDENTIALS_HREF} variant="secondary" color="inherit">
                    Account Settings
                  </Link>
                  .
                </Paragraph>
              </TooltipContent>
            </Tooltip>
          </Paragraph>

          <Box mt="200">
            <Tooltip isDisabled={!hasSingleCredential}>
              <CredentialsSelect
                credentials={credentials}
                selectedCredentialId={selectedCredentialId}
                onSelect={(credential) => setChosenCredentialId(credential.id)}
                label="Choose an API credential to use"
                isDisabled={hasSingleCredential}
              />
              <TooltipContent>
                <Paragraph color="inherit" m="0">
                  You only have one API credential for this workspace. You can create more API credentials within your{' '}
                  <Link href={API_CREDENTIALS_HREF} variant="secondary" color="inherit">
                    Account Settings
                  </Link>
                  .
                </Paragraph>
              </TooltipContent>
            </Tooltip>
          </Box>

          {selectedCredential == null ? null : (
            <>
              <Paragraph mt="300" mb="100" size="xs">
                If you'd like to configure directly in Segment's interface, you'll need the following.
              </Paragraph>
              <Paragraph>
                <CredentialKeyPair
                  credential={selectedCredential}
                  credentialType="Tracking"
                  source={CREDENTIAL_KEY_PAIR_SOURCE}
                />
              </Paragraph>
            </>
          )}

          <Stack alignInline="end">
            <SegmentActionsRow
              state={state}
              selectedCredential={selectedCredential}
              isBusy={isRunning}
              canIntegrate={canIntegrate}
              canEdit={canEdit}
              canDelete={canDelete}
              onConnect={() => {
                if (selectedCredential != null) void connect(selectedCredential);
              }}
              onDisable={() => {
                track('integration', 'integrationDisableStarted', disableStartedProperties('Disabled'));
                setOpenModal('disable');
              }}
              onDelete={() => {
                track('integration', 'integrationDisableStarted', disableStartedProperties('Deleted'));
                setOpenModal('delete');
              }}
            />

            {state !== ConnectionState.Exists ? null : (
              <Tooltip>
                <Text textDecoration="underline" data-test-segment-in-verify-tooltip>
                  Why do we need to verify?
                </Text>
                <TooltipContent>
                  <Paragraph color="inherit" m="0">
                    We need your permission to check Segment's current configuration.
                  </Paragraph>
                </TooltipContent>
              </Tooltip>
            )}
          </Stack>
        </PanelSection>

        <SegmentConnectionSummary
          lastVerifiedAt={lastVerifiedAt}
          connectedSource={connectedSource}
          onOpenClearModal={() => {
            track('integration', 'integrationClearStarted', clearStartedProperties());
            setOpenModal('clear');
          }}
        />

        <SegmentConnectionModals
          openModal={openModal}
          connectedSource={connectedSource}
          isBusy={isRunning}
          canEdit={canEdit}
          canDelete={canDelete}
          onClose={() => setOpenModal(null)}
          onConfirmDisable={() => {
            if (selectedCredential != null) void disable(selectedCredential).finally(() => setOpenModal(null));
          }}
          onConfirmDelete={() => {
            if (selectedCredential != null) void remove(selectedCredential).finally(() => setOpenModal(null));
          }}
          onConfirmClear={() => {
            void clear().finally(() => setOpenModal(null));
          }}
        />
      </Panel>
    </>
  );
}
